import { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  Paragraph,
  Stack,
  Spinner,
  FormControl,
  Select,
} from '@contentful/f36-components';
import { config } from '../config/app-config';
import useMsalToken from '../hooks/useMsalToken';
import ErrorModal from './ErrorModal';

const handleMigrateSection = async (
  sourceSectionId,
  targetTemplateId,
  sdk,
  setIsLoading,
  setIsModalOpen,
  accessToken,
  setErrorMessage,
  setIsErrorModalOpen
) => {
  if (!accessToken) {
    sdk.notifier.error('Authentication token not available. Please wait and try again.');
    return;
  }

  setIsLoading(true);

  try {
    const mode = sdk.parameters.instance.mode;
    const apiUrl = mode === 'prod' ? config.prod.api : config.std.api;
    const response = await fetch(`${apiUrl}/content/migration/section-component`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        sourceSectionId,
        targetTemplateId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || errorData.message || 'Failed to initiate migration');
    }

    sdk.notifier.success('Section migration completed successfully.');
    setIsModalOpen(false);
  } catch (error) {
    sdk.notifier.error(
      error.message || 'An error occurred while migrating the section.'
    );
    setErrorMessage(error.message || 'An error occurred while migrating the section.');
    setIsErrorModalOpen(true);
    setIsModalOpen(false);
  } finally {
    setIsLoading(false);
  }
};

const fetchMigrationConfiguration = async (sdk) => {
  try {
    const entries = await sdk.cma.entry.getMany({
      query: {
        content_type: 'fanHubToFifaPlusMigration',
        limit: 1,
      },
    });

    if (entries.items.length > 0) {
      const configEntry = entries.items[0];
      const state = configEntry.fields.state?.[sdk.locales.default];
      return state && Array.isArray(state) ? state : [];
    }

    return [];
  } catch (error) {
    console.error('Error fetching migration configuration:', error);
    sdk.notifier.error('Failed to load migration configuration.');
    return [];
  }
};

const SectionMigration = ({ sdk, entryId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const accessToken = useMsalToken();

  useEffect(() => {
    setIsLoadingConfig(true);
    fetchMigrationConfiguration(sdk)
      .then((templates) => {
        setTemplates(templates);
      })
      .finally(() => {
        setIsLoadingConfig(false);
      });
  }, [sdk]);

  return (
    <Stack flexDirection="column" alignItems='center' style={{ height: '100vh', gap: '.5rem', justifyContent: isLoadingConfig ? 'center' : 'space-between' }}>
      {isLoadingConfig ? (
        <Stack flexDirection="column" alignItems="center" spacing="spacingS">
          <Spinner size="large" />
          <Paragraph marginBottom='none'>Loading templates...</Paragraph>
        </Stack>
      ) : (
        <>
          <FormControl marginBottom='none' style={{ gap: '.3rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%' }}>
            <FormControl.Label marginBottom='none' style={{ fontSize: '0.675rem' }}>Attach to Template</FormControl.Label>
            <Select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              isDisabled={isLoading || templates.length === 0}
            >
              <Select.Option value="">Select a template...</Select.Option>
              {templates.map((template) => (
                <Select.Option key={template.id} value={template.id}>
                  {template.name}
                </Select.Option>
              ))}
            </Select>
            {templates.length === 0 && !isLoadingConfig && (
              <FormControl.HelpText fontSize="fontSizeS" marginTop='none'>
                No templates available. Please configure migration templates.
              </FormControl.HelpText>
            )}
          </FormControl>

          <Button
            variant="positive"
            onClick={() => setIsModalOpen(true)}
            size='small'
            isDisabled={isLoading || !selectedTemplateId || !accessToken}
            isLoading={isLoading}
          >
            {!accessToken ? 'Authenticating...' : isLoading ? 'Migrating' : 'Migrate Section'}
          </Button> 

          <Modal
            onClose={() => setIsModalOpen(false)}
            isShown={isModalOpen}
            title="Confirm Section Migration"
            size="fullscreen"
          >
            {() => (
              <>
                <Modal.Content style={{ padding: '0.1rem 0.5rem 0 0.5rem' }}>
                  <Paragraph marginBottom="none" style={{ textAlign: 'center' }} fontSize="fontSizeS">
                    Are you sure you want to migrate this Section to FTA? It will be attached to the selected Landing Page template.
                  </Paragraph>
                </Modal.Content>
                <Modal.Controls>
                  <Button
                    variant="transparent"
                    onClick={() => setIsModalOpen(false)}
                    size='small'
                    isDisabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="positive"
                    onClick={() =>
                      handleMigrateSection(
                        entryId,
                        selectedTemplateId,
                        sdk,
                        setIsLoading,
                        setIsModalOpen,
                        accessToken,
                        setErrorMessage,
                        setIsErrorModalOpen
                      )
                    }
                    size='small'
                    isDisabled={isLoading || !accessToken}
                    isLoading={isLoading}
                  >
                    {!accessToken ? 'Authenticating...' : isLoading ? 'Migrating' : 'Confirm'}
                  </Button>
                </Modal.Controls>
              </>
            )}
          </Modal>

          <ErrorModal
            isShown={isErrorModalOpen}
            errorMessage={errorMessage}
            onClose={() => setIsErrorModalOpen(false)}
          />
        </>
      )}
    </Stack>
  );
};

export default SectionMigration;
